import { fontAttrs } from "@/components/tools/printables/page-kit";
import { INK, MUTED } from "@/components/tools/printables/sheet-header";
import type { TablePage } from "@/lib/tool-kit/printables/attendance";
import { r2 } from "@/lib/tool-kit/printables/page";
import {
  type LogColumn,
  type LogColumnLayout,
  type LogEntries,
  ratingOf,
} from "@/lib/tool-kit/printables/reading-log";
import { starPath } from "@/lib/tool-kit/printables/shapes";
import {
  type FontSpec,
  fitLines,
  fitSize,
  type TextMeasure,
  truncateToFit,
} from "@/lib/tool-kit/printables/text";

const SANS: FontSpec = { kind: "sans" };
const BOLD: FontSpec = { kind: "sans", bold: true };
const RULE = "#8a8a8a";
const BAND = "#e9e9e9";

function Stars({
  x,
  y,
  width,
  height,
  filled,
  accent,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  filled: number;
  accent: string;
}) {
  const size = Math.min(height * 0.62, (width - 2) / 5);
  const gap = (width - size * 5) / 6;
  return (
    <g>
      {Array.from({ length: 5 }, (_, index) => (
        <path
          d={starPath(
            r2(x + gap * (index + 1) + size * (index + 0.5)),
            r2(y + height / 2),
            size / 2,
            size * 0.2
          )}
          fill={index < filled ? accent : "none"}
          // biome-ignore lint/suspicious/noArrayIndexKey: the five stars are positional
          key={index}
          stroke={accent}
          strokeLinejoin="round"
          strokeWidth={0.3}
        />
      ))}
    </g>
  );
}

/** One page of the log: heading band, rows (with any typed entries), grid lines, totals row. */
export function LogTable({
  page,
  columns,
  heads,
  entries,
  rowHeight,
  tableHeader,
  right,
  accent,
  measure,
  rowCount,
  totals,
}: {
  page: TablePage;
  columns: readonly LogColumnLayout[];
  heads: Record<LogColumn, string>;
  entries: LogEntries;
  rowHeight: number;
  tableHeader: number;
  right: number;
  accent: string;
  measure: TextMeasure;
  /** Number of data rows; the row after them, if any, is the totals row. */
  rowCount: number;
  totals: {
    label: string;
    pages: string;
    minutes: string;
  } | null;
}) {
  const left = columns[0]?.col.x ?? 0;
  const lastRow = page.rows.at(-1);
  const bottom = lastRow ? lastRow.y + rowHeight : page.headerY + tableHeader;
  const size = Math.min(3.8, rowHeight * 0.5);
  return (
    <g>
      <rect
        fill={BAND}
        height={tableHeader}
        width={r2(right - left)}
        x={r2(left)}
        y={r2(page.headerY)}
      />
      {columns.map(({ key, col }) => {
        const head = heads[key];
        const headSize = fitSize(measure, head, BOLD, col.width - 2, 3.4, 2.4);
        return (
          <text
            fill={INK}
            fontSize={r2(headSize)}
            key={`head-${key}`}
            textAnchor="middle"
            x={r2(col.x + col.width / 2)}
            y={r2(page.headerY + tableHeader * 0.64)}
            {...fontAttrs(BOLD)}
          >
            {truncateToFit(measure, head, BOLD, headSize, col.width - 2)}
          </text>
        );
      })}
      {page.rows.map((row) => {
        const isTotal = row.index >= rowCount;
        const data = entries[row.index] ?? {};
        return (
          <g key={row.index}>
            <line
              stroke={RULE}
              strokeWidth={0.25}
              x1={r2(left)}
              x2={r2(right)}
              y1={r2(row.y + rowHeight)}
              y2={r2(row.y + rowHeight)}
            />
            {columns.map(({ key, col }) => {
              if (isTotal) {
                return (
                  <TotalCell
                    col={col}
                    columnKey={key}
                    isFirst={columns[0]?.key === key}
                    key={`${row.index}-${key}`}
                    label={totals?.label ?? ""}
                    measure={measure}
                    rowHeight={rowHeight}
                    totals={totals}
                    y={row.y}
                  />
                );
              }
              if (key === "rating") {
                return (
                  <Stars
                    accent={accent}
                    filled={ratingOf(data.rating)}
                    height={rowHeight}
                    key={`${row.index}-${key}`}
                    width={col.width}
                    x={col.x}
                    y={row.y}
                  />
                );
              }
              return (
                <CellText
                  col={col}
                  key={`${row.index}-${key}`}
                  measure={measure}
                  rowHeight={rowHeight}
                  size={size}
                  text={data[key] ?? ""}
                  wrap={key === "response"}
                  y={row.y}
                />
              );
            })}
          </g>
        );
      })}
      {columns.map(({ key, col }) => (
        <line
          key={`v-${key}`}
          stroke={RULE}
          strokeWidth={0.25}
          x1={col.x}
          x2={col.x}
          y1={r2(page.headerY)}
          y2={r2(bottom)}
        />
      ))}
      <line
        stroke={RULE}
        strokeWidth={0.25}
        x1={r2(right)}
        x2={r2(right)}
        y1={r2(page.headerY)}
        y2={r2(bottom)}
      />
      <line
        stroke={RULE}
        strokeWidth={0.25}
        x1={r2(left)}
        x2={r2(right)}
        y1={r2(page.headerY)}
        y2={r2(page.headerY)}
      />
    </g>
  );
}

function CellText({
  col,
  text,
  y,
  rowHeight,
  size,
  wrap,
  measure,
}: {
  col: { x: number; width: number };
  text: string;
  y: number;
  rowHeight: number;
  size: number;
  wrap: boolean;
  measure: TextMeasure;
}) {
  if (!text.trim()) {
    return null;
  }
  const width = col.width - 3;
  if (wrap && rowHeight >= 10) {
    const block = fitLines(measure, text.trim(), SANS, width, size, 2.4, 2);
    const total = block.lines.length * block.size * 1.2;
    const first = y + (rowHeight - total) / 2 + block.size * 0.9;
    return (
      <g fill={INK}>
        {block.lines.map((line, index) => (
          <text
            fontSize={block.size}
            key={line}
            x={r2(col.x + 1.5)}
            y={r2(first + index * block.size * 1.2)}
            {...fontAttrs(SANS)}
          >
            {line}
          </text>
        ))}
      </g>
    );
  }
  return (
    <text
      fill={INK}
      fontSize={size}
      x={r2(col.x + 1.5)}
      y={r2(y + rowHeight * 0.64)}
      {...fontAttrs(SANS)}
    >
      {truncateToFit(measure, text.trim(), SANS, size, width)}
    </text>
  );
}

function TotalCell({
  col,
  columnKey,
  isFirst,
  label,
  totals,
  y,
  rowHeight,
  measure,
}: {
  col: { x: number; width: number };
  columnKey: LogColumn;
  /** The label sits in the first column of the row. */
  isFirst: boolean;
  label: string;
  totals: { pages: string; minutes: string } | null;
  y: number;
  rowHeight: number;
  measure: TextMeasure;
}) {
  const baseline = r2(y + rowHeight * 0.64);
  if (isFirst) {
    return (
      <text
        fill={MUTED}
        fontSize={3.6}
        x={r2(col.x + 1.5)}
        y={baseline}
        {...fontAttrs(BOLD)}
      >
        {truncateToFit(measure, label, BOLD, 3.6, col.width - 2)}
      </text>
    );
  }
  const byColumn: Partial<Record<LogColumn, string>> = {
    pages: totals?.pages,
    minutes: totals?.minutes,
  };
  const value = byColumn[columnKey] ?? "";
  return value ? (
    <text
      fill={INK}
      fontSize={3.8}
      textAnchor="middle"
      x={r2(col.x + col.width / 2)}
      y={baseline}
      {...fontAttrs(BOLD)}
    >
      {value}
    </text>
  ) : null;
}
